import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Crown, Lock, Copy, Check, ExternalLink, MousePointerClick, UserPlus,
  CreditCard, CircleDollarSign, Users,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/auth";
import DashNav from "@/components/DashNav";

const EASE = [0.16, 1, 0.3, 1];

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const [stats, setStats] = useState(null);
  const [access, setAccess] = useState(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);

  const isActive = user?.membership_status === "active";
  const [discord, setDiscord] = useState(null);
  const [discordBusy, setDiscordBusy] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const d = searchParams.get("discord");
    if (d === "connected") toast.success("Discord connected — your Elite role is synced.");
    else if (d === "error") toast.error("Discord connection failed — try again.");
    if (d) setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    api.get("/affiliate/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (isActive) {
      api.get("/membership/access").then((r) => setAccess(r.data)).catch(() => {});
      api.get("/discord/status").then((r) => setDiscord(r.data)).catch(() => {});
    }
  }, [isActive]);

  const connectDiscord = async () => {
    setDiscordBusy(true);
    try {
      const res = await api.get("/discord/connect", { params: { origin: window.location.origin } });
      window.location.href = res.data.authorize_url;
    } catch {
      toast.error("Could not start Discord connect");
      setDiscordBusy(false);
    }
  };

  const refLink = stats ? `${window.location.origin}/?ref=${stats.referral_code}` : "";

  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [pwBusy, setPwBusy] = useState(false);

  const changePassword = async (e) => {
    e.preventDefault();
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: curPw, new_password: newPw });
      toast.success("Password updated");
      setCurPw("");
      setNewPw("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update password");
    } finally {
      setPwBusy(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(refLink);
    setCopied(true);
    toast.success("Referral link copied");
    setTimeout(() => setCopied(false), 2000);
  };

  const startCheckout = async () => {
    setBusy(true);
    try {
      const res = await api.post("/payments/checkout", { origin_url: window.location.origin });
      window.location.href = res.data.checkout_url;
    } catch {
      toast.error("Could not start checkout — try again.");
      setBusy(false);
    }
  };

  const openPortal = async () => {
    setBusy(true);
    try {
      const res = await api.post("/payments/portal", { origin_url: window.location.origin });
      window.location.href = res.data.portal_url;
    } catch {
      toast.error("No billing account found yet.");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#08080A] text-[#F4F4F5] grain">
      <DashNav />
      <main data-testid="member-dashboard" className="max-w-6xl mx-auto px-6 py-12 space-y-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }}>
          <p className="mono text-[10px] tracking-[0.3em] uppercase text-[#8A7322] mb-2">Member Dashboard</p>
          <h1 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">
            Welcome, <span className="gold-text">{user?.name?.split(" ")[0] || "Member"}</span>
          </h1>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Membership status */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1, ease: EASE }}
            className="bg-[#101014] border border-[#26262F] rounded-2xl p-8"
            data-testid="membership-status-card"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-sub font-bold text-lg">Membership</h2>
              <span
                data-testid="membership-status-badge"
                className={`mono text-[10px] tracking-[0.2em] uppercase px-3 py-1.5 rounded-full ${
                  isActive
                    ? "bg-[rgba(212,175,55,0.12)] text-[#E6C665] border border-[rgba(212,175,55,0.4)]"
                    : "bg-[#15151A] text-zinc-500 border border-[#26262F]"
                }`}
              >
                {isActive ? "Active" : user?.membership_status === "past_due" ? "Past Due" : "Inactive"}
              </span>
            </div>
            {isActive ? (
              <p className="text-sm text-zinc-400 leading-relaxed">
                Full syndicate access is live. Every monitor, feed and flip channel is yours.
              </p>
            ) : (
              <>
                <p className="text-sm text-zinc-400 leading-relaxed mb-6">
                  You're on the outside looking in. Subscribe to unlock all 40+ monitors, price errors, bot feeds and the private Discord.
                </p>
                <button
                  data-testid="dashboard-subscribe-btn"
                  onClick={startCheckout}
                  disabled={busy}
                  className="btn-gold w-full flex items-center justify-center gap-2 font-sub text-sm py-4 rounded-full disabled:opacity-60"
                >
                  <Crown className="w-4 h-4" /> Subscribe — $15.99/mo
                </button>
              </>
            )}
          </motion.div>

          {/* Discord access */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.18, ease: EASE }}
            className="bg-[#101014] border border-[rgba(212,175,55,0.35)] rounded-2xl p-8"
            data-testid="discord-access-card"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-sub font-bold text-lg">Discord Access</h2>
              {isActive ? (
                <Crown className="w-5 h-5 text-[#D4AF37]" />
              ) : (
                <Lock className="w-5 h-5 text-zinc-600" />
              )}
            </div>
            {isActive ? (
              <>
                {discord?.connected ? (
                  <div className="flex items-center gap-3 bg-[rgba(212,175,55,0.08)] border border-[rgba(212,175,55,0.4)] rounded-xl px-5 py-4 mb-5">
                    <Check className="w-5 h-5 text-[#D4AF37] shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-sub font-semibold truncate" data-testid="discord-connected-text">
                        Connected as @{discord.discord_username}
                      </p>
                      <p className="text-xs text-zinc-500">Bot auto-joined you and synced your Elite role.</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-zinc-400 mb-5">
                    Link your Discord and the bot auto-joins you to the server with the Elite role — every monitor and feed unlocks instantly.
                  </p>
                )}
                <button
                  data-testid="discord-connect-btn"
                  onClick={connectDiscord}
                  disabled={discordBusy}
                  className="btn-gold w-full flex items-center justify-center gap-2 font-sub text-sm py-4 rounded-full disabled:opacity-60"
                >
                  {discord?.connected ? "Reconnect Discord" : "Connect Discord"} <ExternalLink className="w-4 h-4" />
                </button>
                <div className="mt-6 pt-5 border-t border-[#26262F]">
                  <p className="mono text-[10px] tracking-[0.25em] uppercase text-zinc-600 mb-3">Or join manually</p>
                  <div className="bg-[#08080A] border border-[#26262F] rounded-full px-5 py-3 mb-4 overflow-hidden">
                    <span data-testid="dashboard-discord-invite-link" className="mono text-xs text-[#E6C665] truncate block">
                      {access?.paid_discord_invite || "Invite link being finalized — ping staff."}
                    </span>
                  </div>
                  {access?.paid_discord_invite && (
                    <a
                      data-testid="dashboard-discord-join-btn"
                      href={access.paid_discord_invite}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-ghost-gold w-full flex items-center justify-center gap-2 font-sub font-semibold text-sm py-3.5 rounded-full"
                    >
                      Join via Invite <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                </div>
              </>
            ) : (
              <p className="text-sm text-zinc-500 leading-relaxed">
                The private invite unlocks the moment your first payment confirms. No waiting on staff — it's instant.
              </p>
            )}
          </motion.div>
        </div>

        {/* Affiliate hub */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.26, ease: EASE }}
          className="bg-[#101014] border border-[#26262F] rounded-2xl p-8"
          data-testid="affiliate-hub"
        >
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <h2 className="font-sub font-bold text-lg">Affiliate Hub</h2>
            <span className="mono text-[10px] tracking-[0.2em] uppercase text-[#E6C665]">
              Earn {stats?.commission_percent ?? 20}% per paid referral
            </span>
          </div>
          <p className="text-sm text-zinc-400 mb-5 max-w-2xl">
            Share your link anywhere. When someone subscribes through it, you earn commission on their membership.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 mb-8">
            <div className="flex-1 bg-[#08080A] border border-[#26262F] rounded-full px-5 py-3.5 overflow-hidden">
              <span data-testid="affiliate-referral-link-input" className="mono text-xs text-zinc-300 truncate block">
                {refLink || "Loading your link..."}
              </span>
            </div>
            <button
              data-testid="affiliate-copy-link-btn"
              onClick={copyLink}
              className="btn-ghost-gold flex items-center justify-center gap-2 font-sub font-semibold text-sm px-6 py-3.5 rounded-full shrink-0"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />} {copied ? "Copied" : "Copy Link"}
            </button>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: MousePointerClick, label: "Clicks", value: stats?.clicks ?? "—", testid: "affiliate-stats-clicks" },
              { icon: UserPlus, label: "Signups", value: stats?.signups ?? "—", testid: "affiliate-stats-signups" },
              { icon: Users, label: "Paid Referrals", value: stats?.paid_conversions ?? "—", testid: "affiliate-stats-paid" },
              { icon: CircleDollarSign, label: "Pending Payout", value: stats ? `$${(stats.earnings_pending ?? 0).toFixed(2)}` : "—", testid: "affiliate-stats-earnings", sub: stats ? `$${(stats.earnings_paid ?? 0).toFixed(2)} already paid out` : "" },
            ].map((s) => (
              <div key={s.label} className="chip rounded-xl p-5" data-testid={s.testid}>
                <s.icon className="w-4 h-4 text-[#D4AF37] mb-3" />
                <p className="font-sub font-bold text-2xl">{s.value}</p>
                <p className="mono text-[10px] tracking-[0.2em] uppercase text-zinc-500 mt-1">{s.label}</p>
                {s.sub && <p className="text-[10px] text-zinc-600 mt-1">{s.sub}</p>}
              </div>
            ))}
          </div>
        </motion.div>

        {/* Billing */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.34, ease: EASE }}
          className="bg-[#101014] border border-[#26262F] rounded-2xl p-8 flex flex-wrap items-center justify-between gap-6"
          data-testid="billing-card"
        >
          <div>
            <h2 className="font-sub font-bold text-lg mb-2">Billing</h2>
            <p className="text-sm text-zinc-400">
              {isActive
                ? "Manage your subscription, payment method or cancel anytime through Stripe."
                : "No active subscription. Subscribe to unlock billing management."}
            </p>
          </div>
          {isActive && (
            <button
              data-testid="billing-manage-btn"
              onClick={openPortal}
              disabled={busy}
              className="btn-ghost-gold flex items-center gap-2 font-sub font-semibold text-sm px-6 py-3.5 rounded-full disabled:opacity-60"
            >
              <CreditCard className="w-4 h-4" /> Manage Subscription
            </button>
          )}
          {!isActive && (
            <button
              onClick={refresh}
              data-testid="billing-refresh-btn"
              className="btn-ghost-gold text-xs font-sub px-5 py-3 rounded-full"
            >
              Refresh Status
            </button>
          )}
        </motion.div>

        {/* Security */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.42, ease: EASE }}
          className="bg-[#101014] border border-[#26262F] rounded-2xl p-8"
          data-testid="security-card"
        >
          <h2 className="font-sub font-bold text-lg mb-2 flex items-center gap-2">
            <Lock className="w-4 h-4 text-[#D4AF37]" /> Password &amp; Security
          </h2>
          <p className="text-sm text-zinc-500 mb-6">Change the password on your Card Cartel account.</p>
          <form onSubmit={changePassword} className="grid sm:grid-cols-[1fr_1fr_auto] gap-3">
            <input
              data-testid="security-current-password"
              type="password"
              required
              value={curPw}
              onChange={(e) => setCurPw(e.target.value)}
              placeholder="Current password"
              className="bg-[#08080A] border border-[#26262F] rounded-full px-5 py-3.5 text-sm outline-none focus:border-[#D4AF37] transition-colors placeholder:text-zinc-600"
            />
            <input
              data-testid="security-new-password"
              type="password"
              required
              minLength={6}
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              placeholder="New password (min 6 chars)"
              className="bg-[#08080A] border border-[#26262F] rounded-full px-5 py-3.5 text-sm outline-none focus:border-[#D4AF37] transition-colors placeholder:text-zinc-600"
            />
            <button
              data-testid="security-change-btn"
              type="submit"
              disabled={pwBusy}
              className="btn-gold font-sub text-sm px-6 py-3.5 rounded-full disabled:opacity-60"
            >
              Update
            </button>
          </form>
        </motion.div>
      </main>
    </div>
  );
}
